import { Button } from "@controls";
import { UnsurePenguin } from "@illustrations";
import {
  useHasAllPermissions,
  useHasAnyPermission,
  useHasPermission,
} from "@src/modules/member/hooks/use-permissions";
import { DASHBOARD_PATH } from "@src/modules/member/modules/main/router/paths";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface RoutePermissionGuardProps {
  permission?: string;
  any?: string[];
  all?: string[];
  homePath?: string;
  children: ReactNode;
}

export default function RoutePermissionGuard({
  permission,
  any: anyPermissions,
  all: allPermissions,
  homePath = DASHBOARD_PATH(),
  children,
}: RoutePermissionGuardProps) {
  const hasSingle = useHasPermission(permission ?? "");
  const hasAny = useHasAnyPermission(anyPermissions ?? []);
  const hasAll = useHasAllPermissions(allPermissions ?? []);

  const isAllowed = permission
    ? hasSingle
    : anyPermissions
      ? hasAny
      : allPermissions
        ? hasAll
        : false;

  if (!isAllowed) {
    return <ResourceAccessDenied homePath={homePath} />;
  }

  return <>{children}</>;
}

function ResourceAccessDenied({ homePath }: { homePath: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canGoBack = window.history.length > 1;

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="flex flex-col items-center gap-6 max-w-md px-4 text-center">
        <div className="w-full max-w-[300px]">
          <UnsurePenguin />
        </div>
        <h2 className="text-xl font-semibold text-gray-800">
          {t("resourceAccessDeniedTitle")}
        </h2>
        <p className="text-gray-500">{t("resourceAccessDeniedDescription")}</p>
        <div className="flex flex-col gap-3 w-full">
          {canGoBack && (
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
            >
              {t("goBack")}
            </Button>
          )}
          <Button type="button" onClick={() => navigate(homePath)}>
            {t("takeMeHome")}
          </Button>
        </div>
      </div>
    </div>
  );
}
